import * as React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { useForm as useRcForm, FormInstance as RcFormInstance } from 'rc-field-form';
import scrollIntoView from 'scroll-into-view-if-needed';
import lodashGet from 'lodash/get';
import lodashSet from 'lodash/set';
import { ScrollOptions, NamePath, InternalNamePath } from '../utils/interface';
import { toArray, getFieldId, cascadeHandler } from '../utils/util';
export interface FormInstance<Values = any> extends RcFormInstance<Values> {
  scrollToField: (name: NamePath, options?: ScrollOptions) => void;
  /** This is an internal usage. Do not use in your prod */
  __INTERNAL__: {
    /** No! Do not use this in your code! */
    name?: string;
    allDependency?: Object;
    changeHandler?: Object;
    locale?: Object;
    /** No! Do not use this in your code! */
    itemRef: (name: InternalNamePath) => (node: React.ReactElement) => void;
  };
  getFieldInstance: (name: NamePath) => any;
  setFieldValue: (value: any) => void;
  setFieldsValue: (value: any) => void;
  setFieldSource: (value: any) => void;
  setFieldVisible: (value: any) => void;
  setFieldDisabled: (value: any) => void;
  setFieldRequired: (value: any) => void;
  setFieldsDisabled: (value: boolean) => void;
  getFormValue: (nameList?: any, filterFunc?: any) => any;
}

function toNamePathStr(name: NamePath) {
  const namePath = toArray(name);
  return namePath.join('_');
}

// 处理特殊组件的值 (moment转string等)
function handleSpecialComponentVal(instance: any, element: any, originValue: any, key: string) {
  if (instance['componentName'] || instance.constructor['componentName']) {
    const { props } = instance;
    const componentName = instance['componentName'] || instance.constructor['componentName'];
    switch( componentName) {
      case 'Picker':
        if (element) {
          lodashSet(originValue, key, element.format(props.format || 'YYYY-MM-DD'));
        }
        break;
      case 'TimePicker':
        if (element) {
          lodashSet(originValue, key, element.format(props.format || 'HH:mm:ss'));
        }
        break;
      case 'RangePicker':
        if (element) {
          lodashSet(originValue, key, element.map(v => v.format(props.format || 'YYYY-MM-DD HH:mm:ss')));
        }
        break;
      case 'RichEditor':
        if (element) {
          lodashSet(originValue, key, element.toHTML());
        }
        break;
      case 'Table':
      case 'TableEdit':
        lodashSet(originValue, key, (instance as any).state.dataSource || []);
        break;
      default:
        break;
    }
  }
}

export default function useForm<Values = any>(form?: FormInstance<Values>): [FormInstance<Values>] {
  const [rcForm] = useRcForm();
  const itemsRef = React.useRef<Record<string, React.ReactElement>>({});

  const wrapForm: FormInstance<Values> = React.useMemo(
    () =>
      form || {
        ...rcForm,
        __INTERNAL__: {
          itemRef: (name: InternalNamePath) => (node: React.ReactElement) => {
            const namePathStr = toNamePathStr(name);
            if (node) {
              itemsRef.current[namePathStr] = node;
            } else {
              delete itemsRef.current[namePathStr];
            }
          },
          allDependency: {},
          changeHandler: {},
        },
        scrollToField: (name: string, options: ScrollOptions = {}) => {
          const namePath = toArray(name);
          const fieldId = getFieldId(namePath, wrapForm.__INTERNAL__.name);
          const node: HTMLElement | null = fieldId ? document.getElementById(fieldId) : null;

          if (node) {
            scrollIntoView(node, {
              scrollMode: 'if-needed',
              block: 'nearest',
              ...options,
            });
          }
        },
        getFieldInstance: (name: string) => {
          const namePathStr = toNamePathStr(name);
          return itemsRef.current[namePathStr];
        },
        setFieldValue: (val: any) => {
          rcForm.setFieldsValue(val);
        },
        setFieldSource: (value: any) => {
          const {changeHandler} = wrapForm.__INTERNAL__;
          Object.keys(value).forEach(v => {
            if (changeHandler[v]) {
              changeHandler[v](value[v], v, 'source');
            }
          });
        },
        setFieldVisible: (value: any) => {
          const {changeHandler} = wrapForm.__INTERNAL__;
          Object.keys(value).forEach(v => {
            changeHandler[v] && changeHandler[v](value[v], v, 'visible');
          });
        },
        setFieldRequired: (value: any) => {
          const {changeHandler} = wrapForm.__INTERNAL__;
          Object.keys(value).forEach(v => {
            changeHandler[v] && changeHandler[v](value[v], v, 'required');
          });
        },
        setFieldDisabled: (value: any) => {
          const {changeHandler} = wrapForm.__INTERNAL__;
          Object.keys(value).forEach(v => {
            changeHandler[v] && changeHandler[v](value[v], v, 'disabled');
          });
        },
        setFieldsDisabled: (value: boolean) => {
          const {changeHandler} = wrapForm.__INTERNAL__;
          const allValues = rcForm.getFieldsValue();
          Object.keys(allValues).forEach(v => {
            changeHandler[v] && changeHandler[v](value, v, 'disabled');
          });
        },
        getFormValue: (nameList, filterFunc) => {
          const originValue = cloneDeep(rcForm.getFieldsValue(nameList, filterFunc));
          for (const key in originValue) {
            if (Object.prototype.hasOwnProperty.call(originValue, key)) {
              const element = originValue[key];
              const namePathStr = toNamePathStr(key);
              const instance = itemsRef.current[namePathStr];
              const instances: Array<any> = instance ? [{ name: namePathStr, instance, element }] : [];
              // 兼容Form.List输出的数组结构
              if (Array.isArray(element) && itemsRef.current && instances.length <= 0) {
                Object.keys(itemsRef.current).forEach(refKey => {
                  if (refKey.includes(namePathStr)) {
                    const path = refKey.replace(/_/g, '.');
                    instances.push({
                      name: path,
                      instance: itemsRef.current[refKey],
                      element: lodashGet(originValue, path)
                    });
                  }
                });
              }
              if (instances.length <= 0) {
                continue;
              }

              instances.forEach(ref => {
                handleSpecialComponentVal(ref.instance, ref.element, originValue, ref.name);
              });
            }
          }
          return originValue;
        },
        setFieldsValue: (value: any) => {
          const {allDependency} = wrapForm.__INTERNAL__;

          rcForm.setFieldsValue(value);

          if (Object.keys(allDependency).length) {
            const allValues = rcForm.getFieldsValue();
            cascadeHandler(wrapForm, allValues, allDependency);
          }
        }
      },
    [form, rcForm],
  );

  return [wrapForm];
}



// WEBPACK FOOTER //
// ./src/components/simple-form/hooks/useForm.ts